import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useRegisterMutation } from "../features/auth/authApi";

const Register = ({ setOpenLoginModal, setOpenRegisterModal, setOpenVerifyEmailModal, setVerificationEmail }) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [register] = useRegisterMutation();

  const initialValues = {
    name: "",
    phone: "",
    login: "",
    email: "",
    password: "",
    password_confirmation: "",
  };

  const validationSchema = Yup.object({
    name: Yup.string().required(t("registerComponent.errors.nameRequired")),
    phone: Yup.string().required("Հեռախոսահամարը պարտադիր է"),
    login: Yup.string().required("Login is required"),
    email: Yup.string()
      .email(t("registerComponent.errors.emailInvalid"))
      .required(t("registerComponent.errors.emailRequired")),
    password: Yup.string().required(
      t("registerComponent.errors.passwordRequired")
    ),
    password_confirmation: Yup.string()
      .oneOf(
        [Yup.ref("password"), null],
        t("registerComponent.errors.passwordsMustMatch")
      )
      .required(t("registerComponent.errors.passwordConfirmationRequired")),
  });   

  const handleSubmit = async (values, { setSubmitting, resetForm, setFieldError }) => {
    setMessage(null);

    try {
      const data = await register({
        ...values,
        login: values.login,
      }).unwrap();
      setMessage({
        status: true,
        message: data?.message || t("registerComponent.success"),
      });
      resetForm();
      setOpenRegisterModal(false);
      setVerificationEmail(values.email);
      setOpenVerifyEmailModal(true);
    } catch (error) {
      const validationErrors = error?.data?.errors || error?.data?.data?.errors;
      if (validationErrors && typeof validationErrors === "object") {
        Object.entries(validationErrors).forEach(([field, errors]) => {
          setFieldError(field, Array.isArray(errors) ? errors[0] : errors);
        });
      }
      setMessage({
        status: false,
        message:
          error?.data?.message ||
          error?.data?.error ||
          t("registerComponent.errors.registerFailed"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <>
      {/* ================================== Account Page Start =========================== */}
      <section className="account d-flex">
        <div className="account__right register-account-right padding-t-40 padding-b-40 flx-align align-items-start">
          <div className="dark-light-mode">
            {/* Light Dark Mode */}
            {/* <ThemeToggle /> */}
          </div>
          <div className="account-content">
            <Link to="/" className="logo">
              <img
                src="assets/images/logo/logo.png"
                alt=""
                className="white-version"
              />
              <img
                src="assets/images/logo/white-logo.png"
                alt=""
                className="dark-version"
              />
            </Link>
            <h4 className="account-content__title text-capitalize">
              {t("registerComponent.title")}
            </h4>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting }) => (
                <Form autoComplete="off" className="row">
                  {message && (
                    <div
                      className={`col-12 ${message.status ? "text-success" : "text-danger"
                        }`}
                    >
                      {message.message}
                    </div>
                  )}
                  <div className="col-12">
                    <label
                      htmlFor="name"
                      className="form-label font-18 font-heading fw-600"
                    >
                      {t("registerComponent.name")}
                    </label>
                    <div className="position-relative">
                      <Field
                        type="text"
                        name="name"
                        className="common-input common-input--bg common-input--withIcon"
                        id="name"
                        placeholder={t("registerComponent.namePlaceholder")}
                      />
                      <span className="input-icon">
                        <img src="assets/images/icons/user-icon.svg" alt="" />
                      </span>
                    </div>
                    <ErrorMessage
                      name="name"
                      component="div"
                      className="text-danger"
                    />
                  </div>
                  <div className="col-12">
                    <label htmlFor="login" className="form-label font-18 font-heading fw-600">Մուտքանուն</label>
                    <div className="position-relative">
                      <Field type="text" name="login" className="common-input common-input--bg common-input--withIcon" id="login" placeholder="Մուտքանուն" autoComplete="username" />
                      <span className="input-icon"><img src="assets/images/icons/user-icon.svg" alt="" /></span>
                    </div>
                    <ErrorMessage name="login" component="div" className="text-danger" />
                  </div>
                  <div className="col-12">
                    <label
                      htmlFor="phone"
                      className="form-label font-18 font-heading fw-600"
                    >
                      Հեռախոսահամար
                    </label>
                    <div className="position-relative">
                      <Field
                        type="tel"
                        name="phone"
                        className="common-input common-input--bg common-input--withIcon"
                        id="phone"
                        placeholder="+374..."
                        autoComplete="tel"
                      />
                      <span className="input-icon">
                        <i className="las la-phone" />
                      </span>
                    </div>
                    <ErrorMessage
                      name="phone"
                      component="div"
                      className="text-danger"
                    />
                  </div>
                  <div className="col-12">
                    <label
                      htmlFor="email"
                      className="form-label font-18 font-heading fw-600"
                    >
                      {t("registerComponent.email")}
                    </label>
                    <div className="position-relative">
                      <Field
                        type="email"
                        name="email"
                        className="common-input common-input--bg common-input--withIcon"
                        id="email"
                        placeholder={t("registerComponent.emailPlaceholder")}
                      />
                      <span className="input-icon">
                        <img src="assets/images/icons/user-icon.svg" alt="" />
                      </span>
                    </div>
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="text-danger"
                    />
                  </div>
                  <div className="col-12">
                    <label
                      htmlFor="password"
                      className="form-label font-18 font-heading fw-600"
                    >
                      {t("registerComponent.password")}
                    </label>
                    <div className="position-relative">
                      <Field
                        type={showPassword ? "text" : "password"}
                        name="password"
                        className="common-input common-input--bg common-input--withIcon"
                        id="password"
                        placeholder={t("registerComponent.passwordPlaceholder")}
                      />
                      <button type="button" className="input-icon password-toggle" onClick={() => setShowPassword((visible) => !visible)} aria-label={showPassword ? "Hide password" : "Show password"}>
                        <i className={`las ${showPassword ? "la-eye-slash" : "la-eye"}`} />
                      </button>
                    </div>
                    <ErrorMessage
                      name="password"
                      component="div"
                      className="text-danger"
                    />
                  </div>
                  <div className="col-12">
                    <label
                      htmlFor="password_confirmation"
                      className="form-label font-18 font-heading fw-600"
                    >
                      {t("registerComponent.passwordConfirmation")}
                    </label>
                    <div className="position-relative">
                      <Field
                        type={showPasswordConfirmation ? "text" : "password"}
                        name="password_confirmation"
                        className="common-input common-input--bg common-input--withIcon"
                        id="password_confirmation"
                        placeholder={t(
                          "registerComponent.passwordConfirmationPlaceholder"
                        )}
                      />
                      <button type="button" className="input-icon password-toggle" onClick={() => setShowPasswordConfirmation((visible) => !visible)} aria-label={showPasswordConfirmation ? "Hide password" : "Show password"}>
                        <i className={`las ${showPasswordConfirmation ? "la-eye-slash" : "la-eye"}`} />
                      </button>
                    </div>
                    <ErrorMessage
                      name="password_confirmation"
                      component="div"
                      className="text-danger"
                    />
                  </div>
                  <div className="col-12">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn btn-main btn-lg w-100 pill"
                    >
                      {isSubmitting
                        ? t("registerComponent.creatingAccount")
                        : t("registerComponent.createAccount")}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </section>
      {/* ================================== Account Page End =========================== */}
    </>
  );
};

export default Register;
